import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Droplets, Box, Sun, Apple, CheckCircle } from "lucide-react";

const PRODUCT_PULP = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9mvvrwh4_IMG-20260415-WA0050.jpg";
const PRODUCT_CHUNKS = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9gutam0g_IMG-20260415-WA0052.jpg";
const PRODUCT_DRIED = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/4u5e08ja_IMG-20260415-WA0036.jpg";
const PRODUCT_WHOLE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/zb452am0_IMG-20260415-WA0046.jpg";

const products = [
  {
    id: "frozen-pulp",
    title: "Frozen Pineapple Pulp",
    icon: Droplets,
    image: PRODUCT_PULP,
    description:
      "Our frozen pineapple pulp is processed from locally sourced Cayenne pineapples in Hluhluwe, KwaZulu-Natal. The pulp is extracted and frozen to preserve flavour, colour, and nutritional value.",
    applications: [
      "Juice manufacturing",
      "Smoothie production",
      "Beverage concentrates",
      "Flavoured water and health drinks",
      "Food processing ingredients",
    ],
    specs: [
      "Brix level: 12-15",
      "Flash-frozen at -18\u00B0C",
      "Shelf life: 12 months (frozen)",
      "Available in bulk packaging",
    ],
  },
  {
    id: "frozen-chunks",
    title: "Frozen Pineapple Chunks",
    icon: Box,
    image: PRODUCT_CHUNKS,
    description:
      "Our frozen pineapple chunks are irregular-cut and frozen to maintain freshness and quality. Sourced from premium Cayenne pineapples grown in the Hluhluwe region.",
    applications: [
      "Smoothie production",
      "Food service and catering",
      "Bakery and confectionery",
      "Dessert manufacturing",
      "Fruit salad production",
    ],
    specs: [
      "Irregular cut",
      "Frozen",
      "Shelf life: 12 months (frozen)",
      "Available in bulk and retail packaging",
    ],
  },
  {
    id: "dried-pineapple",
    title: "Dried Pineapple",
    icon: Sun,
    image: PRODUCT_DRIED,
    description:
      "Our dried pineapple is produced through controlled dehydration of fresh Cayenne pineapples. The product is preserved, resulting in a shelf-stable offering that retains the essential flavour profile of fresh pineapple.",
    applications: [
      "Snack food manufacturing",
      "Trail mix and granola",
      "Cereal and health bar ingredients",
      "Confectionery and baking",
      "Retail packaged dried fruit",
    ],
    specs: [
      "Preserved",
      "Shelf-stable",
      "Shelf life: 18 months",
      "Available in slices, rings, and diced",
    ],
  },
  {
    id: "whole-fruit",
    title: "Whole Fruit Supply",
    icon: Apple,
    image: PRODUCT_WHOLE,
    description:
      "Fresh whole Cayenne pineapples sourced directly from Mkonge Farm in Hluhluwe. Novapine can supply whole fruit for commercial buyers, distributors, and retailers looking for premium-quality pineapples straight from the farm.",
    applications: [
      "Fresh produce distribution",
      "Retail and farm stall supply",
      "Export markets",
      "Food service and hospitality",
      "Further processing by third parties",
    ],
    specs: [
      "Variety: Cayenne",
      "Origin: Mkonge Farm, Hluhluwe",
      "Sourced directly from grower",
      "Available in bulk and boxed supply",
    ],
  },
];

export default function ProductsPage() {
  return (
    <main data-testid="products-page" className="pt-20">
      {/* Header */}
      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">
            Product Range
          </p>
          <h1
            data-testid="products-heading"
            className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4"
          >
            Our Products
          </h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">
            All products processed from locally sourced Cayenne pineapples in
            Hluhluwe, KwaZulu-Natal.
          </p>
        </div>
      </section>

      {/* Product Details */}
      {products.map((product, index) => (
        <section
          key={product.id}
          data-testid={`product-detail-${product.id}`}
          className={`py-24 ${index % 2 === 0 ? "bg-[#F9F6F0]" : "bg-white"}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                index % 2 === 1 ? "lg:direction-rtl" : ""
              }`}
            >
              {/* Image - alternate sides */}
              <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                <img
                  src={product.image}
                  alt={product.title}
                  className="rounded-xl w-full h-[400px] object-cover"
                />
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-novapine-cream flex items-center justify-center">
                    <product.icon size={20} className="text-novapine-brown" />
                  </div>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown">
                    {product.title}
                  </h2>
                </div>

                <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-8">
                  {product.description}
                </p>

                {/* Applications */}
                <div className="mb-6">
                  <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.1em] text-novapine-gold mb-4">
                    Applications
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {product.applications.map((app) => (
                      <div key={app} className="flex items-center gap-2">
                        <CheckCircle
                          size={14}
                          className="text-novapine-gold shrink-0"
                        />
                        <span className="font-body text-sm text-novapine-brown/70">
                          {app}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Link to={`/products/${product.id}`}>
                  <Button className="btn-primary-novapine rounded-md px-6 py-4 font-heading text-sm tracking-wide">
                    Learn More <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 bg-novapine-brown">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-6">
            Need Product Specifications?
          </h2>
          <p className="font-body text-base text-white/70 leading-relaxed mb-10 max-w-lg mx-auto">
            Contact our team for detailed technical specifications, pricing
            information, and sample availability.
          </p>
          <Link to="/contact">
            <Button
              data-testid="products-cta-button"
              className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold"
            >
              Request Specifications
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
